// Legacy payroll, bit for bit (API `app/Services/Payroll/EmployeePayrollService.php` behind `GET /v1/reports/payroll`,
// and the back office's `ReportsManagementPayroll.vue` / `library/utils.ts` that show it). Money parity first (CLAUDE.md,
// Andrew 2026-10-12): every hour and dollar of Payroll Summary equals the legacy report's to the cent, so the legacy
// arithmetic is the default and the corrections Andrew approved earlier (decide-list E, ledger L7: `computePayroll` in
// payroll.ts, CD-L01…L17) are built but switched off until parity is validated (`DEFAULT_PAYROLL_ARITHMETIC`).
//
// What legacy does, and this reproduces (bugs B-rows cited in field-notes/bugs.md, each fixed only in `decided_fixes`):
// - A card belongs to the 4 AM business day of its clock-in; only cards of the requested range are read, so a range
//   starting mid-week starts the weekly counter at zero (CD-L15 counts the earlier days).
// - A card's hours are (clock-out − clock-in) seconds / 60 / 60 in doubles, less its unpaid and unnamed breaks in whole
//   minutes / 60; an open break deducts nothing. An open card is billed up to `now` (B74; CD-L16 leaves it out).
// - The week is Monday to Sunday whatever the settings say (CD-L03). The 7th consecutive worked day of it takes the
//   7th-day rule: its hours go to overtime (and double time past the daily threshold) unrounded, and it is PAID
//   NOTHING, since that branch never reaches `handlePay` (CD-L01).
// - Every other day: regular up to the daily threshold while the week is under its threshold; overtime past it; double
//   time past a literal 12 overtime hours or the double-time setting once weekly overtime is reached, else past 4 overtime
//   hours (CD-L04, CD-L17); with weekly overtime off, a day's overtime is ALSO added to its regular hours (CD-L14).
// - Each bucket's hours are rounded to 0.01 h (`number_format`), then pay = round(hours × wage, 2) in cents, overtime at
//   wage × 1.5 and double time at wage × 2, keeping hundredths of a cent; the period sums those in doubles, and the back
//   office rounds only when it prints ((cents / 100).toFixed(2)); the Total row truncates its Total Pay to whole cents
//   (`parseInt(x.toFixed(2))`) (CD-L05).
// Every double is emulated exactly by core/money/binary64.ts, PHP's `round` included.
import { money, ratio, type Money } from '@radish/primitives';
import type { Instant } from '../aggregate/context.js';
import { f64, f64Add, f64Compare, f64Div, f64Fmod, f64Mul, f64Sub, jsToFixedScaled, phpRound, toF64, type F64 } from '../money/binary64.js';
import { allocate } from '../money/allocate.js';
import { round } from '../money/rounding.js';
import { addDays, weekdayOf, type BusinessDate } from '../time/index.js';
import type { TimecardBreak, TimecardId } from './timecard/types.js';
import type { PayrollRules } from './payroll.js';

/** Which payroll arithmetic Payroll Summary runs: legacy's (the default, parity first) or the approved corrections. */
export type PayrollArithmetic = 'legacy_binary64' | 'decided_fixes';
/** Switched to `decided_fixes` only after parity is validated against the legacy report (L7 after parity). */
export const DEFAULT_PAYROLL_ARITHMETIC: PayrollArithmetic = 'legacy_binary64';

/**
 * A PHP number: its exact double and whether PHP holds it as an int. Only one branch of legacy reads the type
 * (`$regularHoursWorked !== 0` is false for the int 0 alone), but carrying it keeps the port literal.
 */
type Php = { readonly v: F64; readonly int: boolean };
const int = (n: bigint): Php => ({ v: f64(n), int: true });
const float = (v: F64): Php => ({ v, int: false });
const ZERO = int(0n);
const add = (a: Php, b: Php): Php => (a.int && b.int ? int(a.v.num + b.v.num) : float(f64Add(a.v, b.v)));
const sub = (a: Php, b: Php): Php => (a.int && b.int ? int(a.v.num - b.v.num) : float(f64Sub(a.v, b.v)));
const mul = (a: Php, b: Php): Php => (a.int && b.int ? int(a.v.num * b.v.num) : float(f64Mul(a.v, b.v)));
/** PHP `/`: an int when both are ints and it divides exactly, else a double. */
const div = (a: Php, b: Php): Php => {
  if (a.int && b.int && a.v.num % b.v.num === 0n) return int(a.v.num / b.v.num);
  return float(f64Div(a.v, b.v));
};
const cmp = (a: Php, b: Php): -1 | 0 | 1 => f64Compare(a.v, b.v);
const gt = (a: Php, b: Php): boolean => cmp(a, b) > 0;
const ge = (a: Php, b: Php): boolean => cmp(a, b) >= 0;
const lt = (a: Php, b: Php): boolean => cmp(a, b) < 0;
/** PHP `(float) number_format($x, 2)` and `round($x, 2)`. */
const round2 = (a: Php): Php => float(phpRound(a.v, 2));
const ONE_AND_A_HALF = float({ num: 3n, den: 2n });

export type LegacyPayrollCard = {
  readonly timecardId: TimecardId;
  readonly businessDate: BusinessDate;
  readonly clockedInAt: Instant;
  /** Absent: still clocked in, billed up to `now` as legacy does. */
  readonly clockedOutAt?: Instant;
  readonly breaks: readonly TimecardBreak[];
};

export type LegacyPayrollInput = {
  readonly rules: PayrollRules;
  readonly from: BusinessDate;
  readonly to: BusinessDate;
  /** The employee's cards of business dates `from`…`to` (legacy reads no others). */
  readonly cards: readonly LegacyPayrollCard[];
  /** The employee's hourly wage in cents (legacy `employees.wage`, the current one for every day). */
  readonly wageMinor: bigint;
  /** The instant an open card is billed up to. */
  readonly now: Instant;
};

/** One business day as legacy returns it (`dates[Y-m-d]`): each figure the exact double PHP holds. */
export type LegacyPayrollDay = {
  readonly businessDate: BusinessDate;
  readonly seventhDay: boolean;
  readonly hours: F64;
  readonly regularHours: F64;
  readonly overtimeHours: F64;
  readonly doubleTimeHours: F64;
  /** Cents, to 0.01 ¢ (zero on a 7th day). */
  readonly regularPay: F64;
  readonly overtimePay: F64;
  readonly doubleTimePay: F64;
};

export type LegacyPayroll = {
  readonly days: readonly LegacyPayrollDay[];
  readonly totals: { readonly hours: F64; readonly regularHours: F64; readonly overtimeHours: F64; readonly doubleTimeHours: F64 };
  /** Cents as doubles: the day values summed in date order. */
  readonly pay: { readonly regular: F64; readonly overtime: F64; readonly doubleTime: F64 };
};

const SECOND = 1000n;

/** Legacy seconds of an instant (MySQL datetime: whole seconds). */
const seconds = (at: Instant): bigint => (at >= 0n ? at / SECOND : -((-at + SECOND - 1n) / SECOND));

/** One card's hours (`getTimecardsInCurrentDayRange`): getHourOffset(in, out) − unpaid break minutes / 60. */
function cardHours(card: LegacyPayrollCard, now: Instant): Php {
  const out = card.clockedOutAt ?? now;
  const span = int(seconds(out) - seconds(card.clockedInAt));
  const hours = div(div(span, int(60n)), int(60n));
  // `(int) $breakTaken->break_in_at?->diffInMinutes($breakTaken->break_out_at, true)`: whole minutes, 0 while open.
  let minutes = 0n;
  for (const b of card.breaks) {
    // Legacy's breaks come through `leftJoin employee_breaks … where paid = false or break_id = 0`: a break whose definition
    // row is missing matches neither, so it is never deducted (B423, kept for parity). A deleted definition's row still
    // joins (no soft-delete scope there), so its break is.
    if (b.kind === 'paid' || b.endedAt === undefined || b.definitionMissing === true) continue;
    const d = seconds(b.endedAt) - seconds(b.startedAt);
    minutes += (d < 0n ? -d : d) / 60n;
  }
  const unpaid = minutes > 0n ? div(int(minutes), int(60n)) : ZERO;
  return sub(hours, unpaid);
}

/** `EmployeePayrollService::evaluateWorkHours` for one employee over [from, to]. */
export function computeLegacyPayroll(input: LegacyPayrollInput): LegacyPayroll {
  const { rules } = input;
  const D = div(int(BigInt(rules.dailyOvertime.afterMinutes)), int(60n)); // overtime_hours_day
  const W = div(int(BigInt(rules.weeklyOvertime.afterMinutes)), int(60n)); // overtime_hours_week
  const DT = div(int(BigInt(rules.dailyDoubleTime.afterMinutes)), int(60n)); // double_time_hours_day
  const wage = int(input.wageMinor);
  const byDate = new Map<string, LegacyPayrollCard[]>();
  for (const c of [...input.cards].sort((a, b) => (a.clockedInAt < b.clockedInAt ? -1 : a.clockedInAt > b.clockedInAt ? 1 : 0))) {
    if (c.businessDate < input.from || c.businessDate > input.to) continue;
    byDate.set(c.businessDate, [...(byDate.get(c.businessDate) ?? []), c]);
  }
  let consecutive = 1;
  let weekRegular: Php = ZERO;
  let firstAtForty = true;
  const days: LegacyPayrollDay[] = [];
  let tHours = ZERO;
  let tRegular = ZERO;
  let tOvertime = ZERO;
  let tDouble = ZERO;
  let pRegular = ZERO;
  let pOvertime = ZERO;
  let pDouble = ZERO;
  // Days before `from` in its workweek hold no cards (legacy loads the original range only), so the loop can start at
  // `from`: they would only have reset the counters, as the initial values do.
  for (let date = input.from; date <= input.to; date = addDays(date, 1)) {
    if (weekdayOf(date) === 1) { // PAY_ROLL_WEEK_START = 'Monday'
      consecutive = 1;
      weekRegular = ZERO;
      firstAtForty = true;
    }
    let hours: Php = ZERO;
    let regular: Php = ZERO;
    let overtime: Php = ZERO;
    let double: Php = ZERO;
    let regularPay: Php = ZERO;
    let overtimePay: Php = ZERO;
    let doublePay: Php = ZERO;
    let dayHours: Php = ZERO;
    for (const card of byDate.get(date) ?? []) dayHours = add(dayHours, cardHours(card, input.now));
    const seventh = rules.seventhDay.enabled && consecutive === 7;
    if (gt(dayHours, ZERO)) {
      hours = dayHours;
      if (seventh) {
        // handleSeventhDayRule: no formatting, no pay.
        if (rules.seventhDay.mode === 'all_ot') {
          overtime = dayHours;
        } else {
          const ot = lt(dayHours, D) ? dayHours : D;
          overtime = ot;
          double = sub(dayHours, ot);
        }
      } else {
        // handleRegularWorkHourBeforeFortyHourRule
        let r: Php = lt(dayHours, D) ? dayHours : D;
        if (lt(weekRegular, W)) regular = r;
        const before = weekRegular;
        weekRegular = add(weekRegular, regular);
        let possibleOvertime = sub(dayHours, r);
        // handleRegularWorkHourAfterFortyHourRule
        if (rules.weeklyOvertime.enabled && ge(weekRegular, W)) {
          if (firstAtForty) {
            r = sub(W, before);
            regular = r;
            possibleOvertime = sub(dayHours, r);
            firstAtForty = false;
          } else {
            possibleOvertime = dayHours;
            r = ZERO;
          }
        }
        // handleOverTimeAndDoubleOverTimeHours
        let possibleDouble: Php = ZERO;
        if (rules.weeklyOvertime.enabled && ge(weekRegular, W)) {
          if (!(r.int && r.v.num === 0n)) {
            if (gt(possibleOvertime, int(12n))) {
              possibleDouble = sub(possibleOvertime, int(12n));
              possibleOvertime = int(12n);
            }
          }
          if (ge(possibleOvertime, DT)) {
            possibleDouble = add(possibleDouble, float(f64Fmod(possibleOvertime.v, DT.v)));
            possibleOvertime = DT;
          }
        } else if (gt(possibleOvertime, int(4n))) {
          possibleDouble = sub(possibleOvertime, int(4n));
          possibleOvertime = int(4n);
        }
        if (gt(possibleOvertime, ZERO)) {
          if (!rules.dailyDoubleTime.enabled) possibleOvertime = add(possibleOvertime, possibleDouble);
          if (!rules.weeklyOvertime.enabled) regular = add(regular, possibleOvertime);
          if (rules.dailyOvertime.enabled) overtime = possibleOvertime;
          if (rules.dailyDoubleTime.enabled) double = possibleDouble;
        }
        // formatWorkDayHours, then handlePay
        hours = round2(hours);
        overtime = round2(overtime);
        double = round2(double);
        regular = round2(regular);
        regularPay = round2(mul(regular, wage));
        overtimePay = round2(mul(overtime, mul(wage, ONE_AND_A_HALF)));
        doublePay = round2(mul(double, mul(wage, int(2n))));
      }
    }
    // handleWorkHoursTotals and handleWorkPayTotals (every day of the original range)
    tHours = add(tHours, hours);
    tOvertime = add(tOvertime, overtime);
    tDouble = add(tDouble, double);
    tRegular = add(tRegular, regular);
    pRegular = add(pRegular, phpRoundOf(regularPay));
    pOvertime = add(pOvertime, phpRoundOf(overtimePay));
    pDouble = add(pDouble, phpRoundOf(doublePay));
    days.push({
      businessDate: date, seventhDay: seventh && gt(dayHours, ZERO), hours: hours.v, regularHours: regular.v, overtimeHours: overtime.v, doubleTimeHours: double.v,
      regularPay: regularPay.v, overtimePay: overtimePay.v, doubleTimePay: doublePay.v,
    });
    // getConsecutiveDaysCount
    consecutive = gt(dayHours, ZERO) ? (consecutive === 7 ? 1 : consecutive) + 1 : 1;
  }
  return {
    days,
    totals: { hours: tHours.v, regularHours: tRegular.v, overtimeHours: tOvertime.v, doubleTimeHours: tDouble.v },
    pay: { regular: pRegular.v, overtime: pOvertime.v, doubleTime: pDouble.v },
  };
}

/** `getFormatPay($x)` on the total side: `round($x, 2)` (an int 0 stays 0). */
const phpRoundOf = (a: Php): Php => (a.v.num === 0n ? a : round2(a));

// ---- the back office's printing (legacy library/utils.ts and ReportsManagementPayroll.vue) ---------------------------

/** `Utils.price(cents)` as whole cents: `(cents / 100).toFixed(2)`, on the double. */
export const legacyPriceCents = (cents: F64): bigint => (cents.num === 0n ? 0n : jsToFixedScaled(f64Div(cents, f64(100n)), 2));

/** `Utils.numberFixed(hours)` as hundredths of an hour: `parseFloat(hours.toFixed(2))`. */
export const legacyHundredths = (hours: F64): bigint => (hours.num === 0n ? 0n : jsToFixedScaled(hours, 2));

/** A day's pay as legacy keeps it, in hundredths of a cent (each day value is the double nearest to one). */
export const hundredthsOfCent = (cents: F64): bigint => round(ratio(cents.num * 100n, cents.den), 'HALF_UP');

/** The double legacy holds for a day's pay of `hundredths` hundredths of a cent. */
export const centsOfHundredths = (hundredths: bigint): F64 => toF64(ratio(hundredths, 100n));

/** One employee's day pay in hundredths of a cent, as the report carries it. */
export type LegacyDayPay = { readonly regular: bigint; readonly overtime: bigint; readonly doubleTime: bigint };

/** One employee's pay sums (PHP `pay`), from their days in date order: `0 + round(day, 2) + …`. */
export function legacyPaySums(days: readonly LegacyDayPay[]): { readonly regular: F64; readonly overtime: F64; readonly doubleTime: F64 } {
  const sum = (pick: (d: LegacyDayPay) => bigint): F64 => days.reduce<Php>((s, d) => add(s, phpRoundOf(float(centsOfHundredths(pick(d))))), ZERO).v;
  return { regular: sum((d) => d.regular), overtime: sum((d) => d.overtime), doubleTime: sum((d) => d.doubleTime) };
}

/** What the employee's row prints, in whole cents (Regular / Overtime / Double Time pay, and Total with the premium). */
export type LegacyPayRow = { readonly regular: bigint; readonly overtime: bigint; readonly doubleTime: bigint; readonly total: bigint; readonly laborCost: bigint };

/**
 * An employee's printed pay: each sum through `Utils.price`, Total as `employeeTotalPay` (regular + overtime + double
 * time + the meal premium, in doubles), and the detail dialog's Total Labor Cost tile (the three without the premium).
 */
export function legacyPayRow(days: readonly LegacyDayPay[], premiumMinor: bigint): LegacyPayRow {
  const s = legacyPaySums(days);
  const three = f64Add(f64Add(s.regular, s.overtime), s.doubleTime);
  return {
    regular: legacyPriceCents(s.regular),
    overtime: legacyPriceCents(s.overtime),
    doubleTime: legacyPriceCents(s.doubleTime),
    total: legacyPriceCents(f64Add(three, f64(premiumMinor))),
    laborCost: legacyPriceCents(three),
  };
}

/** A day's printed pay (the detail dialog's cells): each bucket through `Utils.price`. */
export const legacyDayPayCents = (d: LegacyDayPay): { readonly regular: bigint; readonly overtime: bigint; readonly doubleTime: bigint } => ({
  regular: legacyPriceCents(centsOfHundredths(d.regular)),
  overtime: legacyPriceCents(centsOfHundredths(d.overtime)),
  doubleTime: legacyPriceCents(centsOfHundredths(d.doubleTime)),
});

/**
 * The Total row (`tableTotals`) over the listed employees in their order: each pay column the employees' sums added in
 * doubles and printed; Total Pay `parseInt(Number(regular + overtime + double + premium).toFixed(2))`, whole cents
 * truncated toward zero.
 */
export function legacyTotalsRow(employees: readonly { readonly days: readonly LegacyDayPay[]; readonly premiumMinor: bigint }[]): Omit<LegacyPayRow, 'laborCost'> {
  let regular = f64(0n);
  let overtime = f64(0n);
  let double = f64(0n);
  let premium = f64(0n);
  for (const e of employees) {
    const s = legacyPaySums(e.days);
    regular = f64Add(regular, s.regular);
    overtime = f64Add(overtime, s.overtime);
    double = f64Add(double, s.doubleTime);
    premium = f64Add(premium, f64(e.premiumMinor));
  }
  const sum = f64Add(f64Add(f64Add(regular, overtime), double), premium);
  const fixed = jsToFixedScaled(sum, 2); // toFixed(2) of the cents
  const truncated = fixed / 100n; // parseInt drops the fraction, toward zero
  return { regular: legacyPriceCents(regular), overtime: legacyPriceCents(overtime), doubleTime: legacyPriceCents(double), total: truncated };
}


/** A day's printed Total (the detail dialog's `rowTotalPay`): regular + overtime + double time + the day's premium, in doubles. */
export const legacyDayTotalCents = (d: LegacyDayPay, premiumMinor: bigint): bigint =>
  legacyPriceCents(f64Add(f64Add(f64Add(f64Add(f64(0n), centsOfHundredths(d.regular)), centsOfHundredths(d.overtime)), centsOfHundredths(d.doubleTime)), f64(premiumMinor)));

/** `Utils.numberFixed` of hours summed in doubles in this order (the Total row's hours over the listed employees). */
export const legacyHoursSum = (hours: readonly F64[]): bigint => legacyHundredths(hours.reduce((a, b) => f64Add(a, b), f64(0n)));

/** Whole cents as the report's exact day pay (hundredths of a cent): the corrections' pay has no fraction of a cent. */
export const hundredthsOfWholeCents = (cents: bigint): bigint => cents * 100n;

/** One day of a workweek as the labor rollups store it: hours in hundredths, pay in whole cents. */
export type LegacyLaborDay = {
  readonly businessDate: BusinessDate;
  readonly hours: { readonly worked: bigint; readonly regular: bigint; readonly overtime: bigint; readonly doubleTime: bigint };
  readonly pay: { readonly regular: Money; readonly overtime: Money; readonly doubleTime: Money };
};

/**
 * A Monday-to-Sunday workweek of closed cards as the labor rollups store it, so that every labor figure equals Payroll
 * Summary's (lane D4, 2026-10-13): each day as legacy computes it for a range starting on that Monday (legacy resets its
 * counters every Monday, so a day's figures depend only on its own week's earlier days; a range starting mid-week reads
 * that week differently, legacy B304). Hours are printed as Payroll Summary prints them. A day's pay is its printed
 * Total (the detail's `rowTotalPay` without the meal premium) split over regular, overtime and double time by their
 * exact pay, largest remainder first, so the three add up to the Total to the cent and none is more than a cent from its
 * own exact figure. Open cards are left out: legacy bills them up to the moment it is asked, which no stored figure
 * can be.
 */
export function legacyLaborWeek(input: { readonly rules: PayrollRules; readonly weekStart: BusinessDate; readonly cards: readonly LegacyPayrollCard[]; readonly wage: Money }): LegacyLaborDay[] {
  const closed = input.cards.filter((c) => c.clockedOutAt !== undefined);
  const payroll = computeLegacyPayroll({ rules: input.rules, from: input.weekStart, to: addDays(input.weekStart, 6), cards: closed, wageMinor: input.wage.minor, now: 0n });
  return payroll.days.map((d) => {
    const exact = { regular: hundredthsOfCent(d.regularPay), overtime: hundredthsOfCent(d.overtimePay), doubleTime: hundredthsOfCent(d.doubleTimePay) };
    // rounding-step: labor.legacy_day_total (legacy prints the day's Total once; the buckets share it by largest remainder)
    const [regular, overtime, doubleTime] = allocate(money(legacyDayTotalCents(exact, 0n), input.wage.currency), [exact.regular, exact.overtime, exact.doubleTime]);
    const zero = money(0n, input.wage.currency);
    return {
      businessDate: d.businessDate,
      hours: { worked: legacyHundredths(d.hours), regular: legacyHundredths(d.regularHours), overtime: legacyHundredths(d.overtimeHours), doubleTime: legacyHundredths(d.doubleTimeHours) },
      pay: { regular: regular ?? zero, overtime: overtime ?? zero, doubleTime: doubleTime ?? zero },
    };
  });
}
